
export type DocSearchRecord = {
  pageTitle: string // the page's H1
  url: string // route to the page
  heading?: string // section heading (undefined = page-level)
  anchor: string // '' or '#section-slug'
  text: string // lowercased haystack (heading + body)
  snippet: string // short body preview for display
}

const BASE = '/app-integration-docs'
const DOCS_ROUTE = '/docs/app-integration-development'

// Matches OnThisPage's slugify so section anchors line up with rendered heading ids.
function slugify(text: string): string {
  return text.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-')
}

function stripMd(s: string): string {
  return s
    .replace(/`{1,3}[^`]*`{1,3}/g, ' ') // inline/code spans
    .replace(/[*_>#|]/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1') // links -> text
    .replace(/\s+/g, ' ')
    .trim()
}

async function fetchSidebarSlugs(): Promise<string[]> {
  const md = await fetch(`${BASE}/_Sidebar.md`).then((r) => r.text())
  const slugs = new Set<string>()
  const re = /\/docs\/app-integration-development\/([a-z0-9-/]+)/gi
  let m: RegExpExecArray | null
  while ((m = re.exec(md))) slugs.add(m[1])
  return [...slugs]
}

function parsePage(slug: string, md: string): DocSearchRecord[] {
  const url = `${DOCS_ROUTE}/${slug}`
  const lines = md.split('\n')
  let pageTitle = slug
  const records: DocSearchRecord[] = []
  let heading: string | undefined
  let buf: string[] = []
  let inFence = false

  const flush = () => {
    const body = stripMd(buf.join(' '))
    if (!heading && !body) { buf = []; return } // skip empty lead-in
    records.push({
      pageTitle,
      url,
      heading,
      anchor: heading ? `#${slugify(heading)}` : '',
      // Full body is searchable; the display snippet is trimmed separately.
      text: `${pageTitle} ${heading ?? ''} ${body}`.toLowerCase(),
      snippet: body.slice(0, 140),
    })
    buf = []
  }

  for (const raw of lines) {
    if (/^```/.test(raw.trim())) { inFence = !inFence; continue }
    if (inFence) continue
    const h1 = /^#\s+(.+)/.exec(raw)
    const hx = /^(##|###)\s+(.+)/.exec(raw)
    if (h1) { pageTitle = h1[1].trim(); continue }
    if (hx) { flush(); heading = hx[2].trim(); continue }
    buf.push(raw)
  }
  flush()
  return records
}

let indexPromise: Promise<DocSearchRecord[]> | null = null

export function getDocsIndex(): Promise<DocSearchRecord[]> {
  if (!indexPromise) {
    indexPromise = (async () => {
      try {
        const slugs = await fetchSidebarSlugs()
        const pages = await Promise.all(
          slugs.map(async (slug) => {
            try {
              const md = await fetch(`${BASE}/${slug}.md`).then((r) => (r.ok ? r.text() : ''))
              return md ? parsePage(slug, md) : []
            } catch {
              return []
            }
          }),
        )
        return pages.flat()
      } catch {
        return []
      }
    })()
  }
  return indexPromise
}

export function searchDocs(records: DocSearchRecord[], query: string, limit = 8): DocSearchRecord[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  const terms = q.split(/\s+/)
  const scored: { r: DocSearchRecord; score: number }[] = []
  for (const r of records) {
    let score = 0
    let ok = true
    for (const t of terms) {
      if (!r.text.includes(t)) { ok = false; break }
      if (r.heading && r.heading.toLowerCase().includes(t)) score += 6
      if (r.pageTitle.toLowerCase().includes(t)) score += 4
      score += 1
    }
    if (ok) score += r.heading ? 1 : 0.5 // prefer section-level hits slightly
    if (ok) scored.push({ r, score })
  }
  scored.sort((a, b) => b.score - a.score)
  return scored.slice(0, limit).map((x) => x.r)
}
